/**
 * One ZIP Code's working state: what is typed, which counties the ZIP
 * resolved to, whether a lookup is running, and what the submit does. It is the
 * behaviour of production's `ZipcodeModule`, as a subscribe/getSnapshot store so
 * a component only renders it. The ZIP, fee and counties that must agree across
 * every instance on the page live in `zip-store.ts`.
 *
 * A lookup past 5 s shows "taking a while", past 30 s it aborts; a success
 * message stays 5 s, then the panel collapses.
 */

import { COOKIE_CURRENT_COUNTY, COOKIE_CURRENT_ZIP, COOKIE_RSN_FEE, getCookie, getDmaCookies, getZipWithSignature, setDmaCookies } from "./zip-cookies";
import { countyKey, getCountiesByZip, type ZipCounty } from "./county-lookup";
import { fetchLocalRsnChannels, type LocalRsnOptions, type LocalRsnResult } from "./local-rsn-channels";
import { isValidRsnFee } from "./rsn-fee";
import { isValidFullZip, sanitizeZipInput } from "./zip-validator";
import type { ZipStore } from "./zip-store";

export const COUNTY_HANGING_MS = 5000;
export const COUNTY_TIMEOUT_MS = 30000;
export const SUCCESS_MESSAGE_MS = 5000;

/** Every callback is optional and called guarded: a host that throws cannot break the flow. */
export interface ZipSessionCallbacks {
  /** A valid submit, before the commit. `rsnFee` is the committed ZIP's fee or "" — never a previous ZIP's. */
  onSearchButtonClick?: (
    zip: string,
    counties: ZipCounty[],
    rsnFee: string,
    channels: LocalRsnResult | null,
    county: ZipCounty
  ) => void;
  /** A fee arrived for the committed ZIP. Not called for a failed fetch. */
  onRsnFeeConfirmed?: (fee: string) => void;
  onLocalRsnChnlsConfirmed?: (channels: LocalRsnResult | null, dma: string, nielsenDma: string) => void;
  onLocalRsnChnlsApiError?: (error: unknown) => void;
  onLocalRsnChnlApiLoading?: (loading: boolean) => void;
  /** Channels settled for the page-load ZIP. */
  onCacheConfirmed?: (zip: string, counties: ZipCounty[], rsnFee: string, channels: LocalRsnResult | null) => void;
  onCountyApiError?: (message: string) => void;
}

export interface ZipSessionOptions {
  /** Which channel products to request. Defaults to satellite, as the plan-card topper does. */
  localRsnApiOptions?: LocalRsnOptions;
  /** Fetch local and regional channels with each county lookup. Defaults to true. */
  fetchChannels?: boolean;
  /** Open the panel when no DMA cookie is known yet, as production does. Defaults to true. */
  autoOpenOnFirstVisit?: boolean;
  callbacks?: ZipSessionCallbacks;
}

export interface ZipSessionState {
  isExpanded: boolean;
  inputZip: string;
  displayZip: string;
  displayCounty: ZipCounty | null;
  countyOptions: ZipCounty[];
  /** The selected county's key (fips + DMA), "" for none. */
  selectedCountyKey: string;
  zipInputError: boolean;
  countySelectionError: boolean;
  countyNoResults: boolean;
  countyApiError: boolean;
  countyApiHanging: boolean;
  isCountyApiLoading: boolean;
  showSuccess: boolean;
}

export const INITIAL_SESSION_STATE: ZipSessionState = {
  isExpanded: false,
  inputZip: "",
  displayZip: "",
  displayCounty: null,
  countyOptions: [],
  selectedCountyKey: "",
  zipInputError: false,
  countySelectionError: false,
  countyNoResults: false,
  countyApiError: false,
  countyApiHanging: false,
  isCountyApiLoading: false,
  showSuccess: false
};

type Trigger = "syncload" | "userinput";

interface CachedChannels {
  zip: string;
  dma: string;
  nielsenDma: string;
  data: LocalRsnResult;
}

export function createZipSession(store: ZipStore, initialOptions: ZipSessionOptions = {}) {
  let state: ZipSessionState = INITIAL_SESSION_STATE;
  let options = initialOptions;
  const listeners = new Set<() => void>();

  let started = false;
  let unsubscribe: (() => void) | undefined;
  let lastSyncedStoreZip = "";
  let lastEmittedFee = "";

  // The ZIP the options on screen belong to, "" while unresolved.
  let cachedZipcode = "";
  let cachedDmas: { dma: string; nielsenDma: string } | null = null;
  let lastCountyOptions: ZipCounty[] = [];
  let cachedChannels: CachedChannels | null = null;
  /* The channels a submit is waiting for, bound to the ZIP and DMAs it committed.
     Only submit() sets it; only the fetch for that exact key may deliver it, and
     whatever that fetch ends in (data, a failure, being superseded) delivers once
     and clears it. stop() clears it, and so does a sync to a different committed ZIP. */
  let awaitedChannels: { zip: string; dma: string; nielsenDma: string } | null = null;
  const channelsKey = (zip: string, dma: string, nielsenDma: string) => `${zip}|${dma}|${nielsenDma}`;
  const isAwaited = (zip: string, dma: string, nielsenDma: string) => awaitedChannels !== null && channelsKey(awaitedChannels.zip, awaitedChannels.dma, awaitedChannels.nielsenDma) === channelsKey(zip, dma, nielsenDma);
  // Keys of channels requests running now, so nothing asks twice for one key.
  const channelsInFlight = new Set<string>();

  let lookupSeq = 0;
  let inFlightZip = "";
  let controller: AbortController | undefined;
  let hangTimer: ReturnType<typeof setTimeout> | undefined;
  let abortTimer: ReturnType<typeof setTimeout> | undefined;
  let channelsSeq = 0;
  let channelsDeliveredSeq = 0;
  let channelsCall = 0;
  let successTimer: ReturnType<typeof setTimeout> | undefined;

  const settings = () => ({
    localRsnApiOptions: options.localRsnApiOptions ?? { satellite: true },
    fetchChannels: options.fetchChannels !== false,
    autoOpen: options.autoOpenOnFirstVisit !== false
  });

  function setState(patch: Partial<ZipSessionState>) {
    state = {
      ...state,
      ...patch
    };
    listeners.forEach(listener => listener());
  }

  /** Runs a host callback; a throw is swallowed and reported as `false`. */
  function call<A extends unknown[]>(callback: ((...args: A) => void) | undefined, ...args: A): boolean {
    try {
      callback?.(...args);
      return true;
    } catch {
      return false;
    }
  }

  const callbacks = () => options.callbacks || {};

  function feeFor(zip: string): string {
    const snapshot = store.getSnapshot();
    return snapshot.feeZip === zip && isValidRsnFee(snapshot.rsnFee) ? snapshot.rsnFee : "";
  }

  /**
   * The county the `currentCounty` cookie names, when it is one of these. Resolved
   * against the list, never trusted alone: the cookie carries no ZIP, so after a ZIP
   * change its key can name a county this ZIP does not have.
   */
  function cookieMatch(counties: ZipCounty[]): ZipCounty | null {
    const key = getCookie(COOKIE_CURRENT_COUNTY);
    return key ? counties.find(county => countyKey(county) === key) ?? null : null;
  }

  /** The one county whose DMA matches the DMA cookie, or null unless exactly one does. */
  function dmaMatch(counties: ZipCounty[]): ZipCounty | null {
    const { currentDma } = getDmaCookies();
    const matching = counties.filter(county => county.dtvDMA === currentDma);
    return matching.length === 1 ? matching[0] : null;
  }

  /** The county committed on this page, when it is for these counties' ZIP and among them. */
  function committedMatch(counties: ZipCounty[]): ZipCounty | null {
    const { committedCounty } = store.getSnapshot();
    if (!committedCounty || committedCounty.zipcode !== counties[0]?.zipcode) {
      return null;
    }
    return counties.find(county => countyKey(county) === countyKey(committedCounty)) ?? null;
  }

  /**
   * The county we can name without guessing: the reader's own choice, else a
   * unique DMA-cookie match, else the only one on offer. `null` means the ZIP
   * has several counties and nothing distinguishes them — the reader must pick,
   * and no path may select one for them.
   */
  function knownCounty(counties: ZipCounty[]): ZipCounty | null {
    return committedMatch(counties) ?? cookieMatch(counties) ?? dmaMatch(counties) ??
      (counties.length === 1 ? counties[0] : null);
  }

  /** The county to SHOW: the known one, else the first. Showing a guess is fine; selecting one is not. */
  function pickCounty(counties: ZipCounty[]): ZipCounty {
    return knownCounty(counties) ?? counties[0];
  }

  function selectedCounty(): ZipCounty | null {
    return state.countyOptions.find(county => countyKey(county) === state.selectedCountyKey) ?? null;
  }

  function clearLookupTimers() {
    clearTimeout(hangTimer);
    clearTimeout(abortTimer);
    hangTimer = undefined;
    abortTimer = undefined;
  }

  function cancelLookup() {
    lookupSeq += 1;
    clearLookupTimers();
    controller?.abort();
    controller = undefined;
    inFlightZip = "";
  }

  /* --- Channels ------------------------------------------------------- */

  function deliverChannels(data: LocalRsnResult | null, dma: string, nielsenDma: string): boolean {
    const delivered = call(callbacks().onLocalRsnChnlsConfirmed, data, dma, nielsenDma);
    if (data && Array.isArray(data.localChannels)) {
      store.setLocalsEligible(data.localChannels.length > 0);
    }
    return delivered;
  }

  async function loadChannels(zip: string, dma: string, nielsenDma: string): Promise<LocalRsnResult | null> {
    channelsCall += 1;
    const callId = channelsCall;
    const key = channelsKey(zip, dma, nielsenDma);
    channelsInFlight.add(key);
    call(callbacks().onLocalRsnChnlApiLoading, true);
    try {
      return await fetchLocalRsnChannels({
        zip,
        dma,
        nielsenDma
      }, settings().localRsnApiOptions, store.getConfig());
    } catch (error) {
      // After stop() nothing may fire: the host has unmounted.
      if (started) {
        call(callbacks().onLocalRsnChnlsApiError, error);
      }
      return null;
    } finally {
      channelsInFlight.delete(key);
      // Only the newest call may clear the spinner.
      if (callId === channelsCall && started) {
        call(callbacks().onLocalRsnChnlApiLoading, false);
      }
    }
  }

  /**
   * Fetches channels for a ZIP + DMA pair, or reuses them when the SAME ZIP and
   * DMAs were already fetched — keyed by ZIP, not DMA alone, because the
   * regional list can differ by ZIP inside one DMA.
   *
   * A superseded answer is dropped if a newer one was already delivered. On
   * page load (`onLoad`) the first answer still delivers; otherwise it
   * delivers only if a submit is waiting for it.
   */
  async function cacheLocale(
    zip: string,
    county: ZipCounty,
    onLoad: boolean
  ): Promise<{ data: LocalRsnResult | null; stale: boolean }> {
    if (!settings().fetchChannels) {
      return {
        data: null,
        stale: false
      };
    }
    channelsSeq += 1;
    const seq = channelsSeq;
    const dma = county.dtvDMA;
    const nielsenDma = county.nielsenDMA;
    const reusable = cachedChannels &&
      cachedChannels.zip === zip && cachedChannels.dma === dma && cachedChannels.nielsenDma === nielsenDma;
    const data = reusable ? cachedChannels.data : await loadChannels(zip, dma, nielsenDma);
    if (!started) {
      return {
        data,
        stale: true
      };
    }

    // The fetch a submit waits for delivers whatever it ends in — before any drop below.
    if (isAwaited(zip, dma, nielsenDma)) {
      deliverChannels(data, dma, nielsenDma);
      channelsDeliveredSeq = Math.max(channelsDeliveredSeq, seq);
      awaitedChannels = null;
      if (data) {
        cachedChannels = {
          zip,
          dma,
          nielsenDma,
          data
        };
      }
      return {
        data,
        stale: false
      };
    }

    const superseded = seq !== channelsSeq;
    if (superseded && channelsDeliveredSeq > seq) {
      return {
        data,
        stale: true
      };
    }
    if (superseded) {
      if (onLoad) {
        deliverChannels(data, dma, nielsenDma);
        channelsDeliveredSeq = seq;
      }
    } else if (onLoad) {
      deliverChannels(data, dma, nielsenDma);
      channelsDeliveredSeq = seq;
      if (data) {
        cachedChannels = {
          zip,
          dma,
          nielsenDma,
          data
        };
      }
    } else if (data) {
      cachedChannels = {
        zip,
        dma,
        nielsenDma,
        data
      };
    }
    return {
      data,
      stale: false
    };
  }

  /* --- County lookup -------------------------------------------------- */

  function applyLookupResult(counties: ZipCounty[], zip: string, trigger: Trigger) {
    cachedZipcode = zip;
    // Before any store write: a write re-syncs, and the sync reads these.
    lastCountyOptions = counties;
    const snapshot = store.getSnapshot();
    // Computed before the store write below changes them.
    const storeHadCounties = snapshot.counties.length > 0;
    const storeHadZip = Boolean(snapshot.zip);

    setState({
      countyNoResults: false,
      isCountyApiLoading: false,
      countyApiHanging: false
    });

    if (trigger === "syncload" && (!storeHadCounties || !storeHadZip)) {
      store.updateCounties(counties);
      if (!storeHadZip) {
        const cookieFee = getCookie(COOKIE_RSN_FEE);
        // The cookie fee belongs to the ZIP cookie. For any other ZIP (an edge
        // ZIP, say) it is somebody else's price, so that ZIP is priced afresh.
        /* ⚠ A malformed fee must fall through to the refetch as well. Adopting it
           commits with `fetchFee` false, so the ZIP is never priced, and
           `updateRsnFee` rewrites the bad cookie for another 182 days — leaving a
           browser that can never recover on its own. Production tolerates this
           (`zipcode-module.tsx:597-603` adopts the cookie unchecked) because it
           validates on read; the store already validates on hydrate
           (`zip-store.ts:153`, `:171`), so this is the one adoption path that did not. */
        /* ⚠ And not when this ZIP has already been priced to nothing: `commitZip` writes
           `currentZipcode` before the fetch resolves, so after a failure the two cookies
           agree on the PREVIOUS ZIP's price and nothing else can tell them apart. */
        if (cookieFee && isValidRsnFee(cookieFee) && getCookie(COOKIE_CURRENT_ZIP) === zip && !store.pricedEmpty(zip)) {
          store.updateZip(zip, false);
          store.updateRsnFee(cookieFee, zip);
        } else {
          store.updateZip(zip, true);
        }
      }
    }

    const patch: Partial<ZipSessionState> = { countyOptions: counties };
    // A fresh multi-county answer to typing needs a deliberate pick.
    if (counties.length > 1 && trigger !== "syncload") {
      patch.selectedCountyKey = "";
    }
    const [first] = counties;
    let toSelect: ZipCounty | null = null;
    let toPrefetch: ZipCounty = first;
    if (counties.length === 1) {
      toSelect = first;
      patch.countySelectionError = false;
    }
    if (trigger === "syncload") {
      const known = knownCounty(counties);
      if (known) {
        toSelect = known;
        toPrefetch = known;
      }
      patch.displayCounty = pickCounty(counties);
    }
    if (toSelect) {
      patch.selectedCountyKey = countyKey(toSelect);
      cachedDmas = {
        dma: toSelect.dtvDMA,
        nielsenDma: toSelect.nielsenDMA
      };
    }
    setState(patch);

    return cacheLocale(zip, toPrefetch, trigger === "syncload").then(({ data, stale }) => {
      if (trigger === "syncload" && !stale) {
        call(callbacks().onCacheConfirmed, zip, counties, feeFor(zip), data);
      }
    });
  }

  function applyLookupError() {
    setState({
      countyApiError: true,
      countyApiHanging: false,
      countyNoResults: false,
      isCountyApiLoading: false
    });
    call(callbacks().onCountyApiError, "County data not found");
    // The committed ZIP's fee is still good; re-confirm it for hosts that priced on it.
    const fee = feeFor(store.getSnapshot().zip);
    if (fee) {
      call(callbacks().onRsnFeeConfirmed, fee);
    }
  }

  function search(zip: string, trigger: Trigger) {
    if (zip === cachedZipcode) {
      // Re-entering the ZIP that is already resolved: restore, no request.
      const patch: Partial<ZipSessionState> = { countyOptions: lastCountyOptions };
      // A sync that nulled the pill for a new ZIP finds its counties here already (this
      // instance resolved the ZIP by typing): refill it, as a fresh lookup would.
      if (trigger === "syncload" && !state.displayCounty && lastCountyOptions.length) {
        patch.displayCounty = pickCounty(lastCountyOptions);
      }
      if (!state.selectedCountyKey && lastCountyOptions.length === 1) {
        patch.selectedCountyKey = countyKey(lastCountyOptions[0]);
        patch.countySelectionError = false;
      }
      setState(patch);
      return;
    }
    const snapshot = store.getSnapshot();
    const shared = snapshot.counties;
    if (trigger === "syncload" && snapshot.zip && shared.length && shared[0]?.zipcode === snapshot.zip && snapshot.zip === zip) {
      // Another instance already resolved this ZIP: reuse its counties, no request.
      cancelLookup();
      cachedZipcode = zip;
      const picked = pickCounty(shared);
      if (!state.countyOptions.length || state.countyOptions[0]?.zipcode !== shared[0].zipcode) {
        lastCountyOptions = shared;
        cachedDmas = {
          dma: picked.dtvDMA,
          nielsenDma: picked.nielsenDMA
        };
        /* Only a known county is selected here. Selecting the shown guess would let the
           SECOND instance on a page commit a county the reader never picked, while the
           first — which ran the fresh path — still asks them to choose. */
        const known = knownCounty(shared);
        setState({
          displayCounty: picked,
          selectedCountyKey: known ? countyKey(known) : "",
          countyOptions: shared,
          isCountyApiLoading: false,
          countyApiHanging: false
        });
      }
      void cacheLocale(zip, picked, true).then(({ data, stale }) => {
        if (!stale) {
          call(callbacks().onCacheConfirmed, zip, shared, feeFor(zip), data);
        }
      });
      return;
    }

    // The same ZIP already running (and not answered by the store above): leave it be.
    if (inFlightZip === zip) {
      return;
    }

    // Latest wins: a lookup for another ZIP supersedes whatever is running.
    cancelLookup();
    const seq = lookupSeq;
    const own = new AbortController();
    controller = own;
    inFlightZip = zip;
    cachedZipcode = "";
    cachedDmas = null;
    setState({
      countyOptions: [],
      countyApiError: false,
      countyApiHanging: false,
      isCountyApiLoading: true
    });
    hangTimer = setTimeout(() => {
      if (seq === lookupSeq) {
        setState({ countyApiHanging: true });
      }
    }, COUNTY_HANGING_MS);
    // Aborting rejects the fetch, which resolves the lookup to the error state.
    abortTimer = setTimeout(() => own.abort(), COUNTY_TIMEOUT_MS);

    void getCountiesByZip(zip, {
      channelLookupPath: store.getConfig().channelLookupPath,
      signal: own.signal
    }).then(counties => {
      if (seq !== lookupSeq || !started) {
        return undefined;
      }
      clearLookupTimers();
      inFlightZip = "";
      if (counties === undefined) {
        applyLookupError();
        return undefined;
      }
      if (counties.length === 0) {
        setState({
          countyNoResults: true,
          isCountyApiLoading: false,
          countyApiHanging: false
        });
        return undefined;
      }
      return applyLookupResult(counties, zip, trigger);
    });
  }

  /* --- Syncing with the shared store ---------------------------------- */

  function sync() {
    const best = getZipWithSignature(store.getSnapshot().zip);
    // Another instance committed a different ZIP: the awaited one is stale and must not land on top.
    if (awaitedChannels && awaitedChannels.zip !== best.value) {
      awaitedChannels = null;
    }
    if (best.value === state.displayZip && state.displayCounty && state.isCountyApiLoading) {
      return;
    }
    // An edge or fallback ZIP shows in the pill but leaves the field as it was.
    const patch: Partial<ZipSessionState> = { displayZip: best.value };
    // A new ZIP must not keep the old ZIP's county in the pill; a store that
    // already holds this ZIP's counties refills it in the search below.
    if (best.value !== state.displayZip) {
      patch.displayCounty = null;
    }
    if (best.origin === "context" || best.origin === "currentZipcode") {
      patch.inputZip = best.value;
    }
    const { currentDma, nielsenDma } = getDmaCookies();
    if (!(currentDma || nielsenDma) && settings().autoOpen) {
      patch.isExpanded = true;
    }
    setState(patch);
    search(best.value, "syncload");
  }

  function emitFee() {
    const snapshot = store.getSnapshot();
    const fee = feeFor(snapshot.zip);
    if (fee && fee !== lastEmittedFee) {
      lastEmittedFee = fee;
      call(callbacks().onRsnFeeConfirmed, fee);
    }
  }

  /**
   * A county was committed for the ZIP already on screen: show it in the pill
   * and, unless the panel is open mid-edit, select it. An open panel keeps its
   * own selection and inputs.
   */
  function syncCommittedCounty() {
    const { committedCounty } = store.getSnapshot();
    if (!committedCounty || committedCounty.zipcode !== state.displayZip) {
      return;
    }
    const county = state.countyOptions.find(item => countyKey(item) === countyKey(committedCounty));
    if (!county || (state.displayCounty && countyKey(state.displayCounty) === countyKey(county))) {
      return;
    }
    const patch: Partial<ZipSessionState> = { displayCounty: county };
    if (!state.isExpanded) {
      patch.selectedCountyKey = countyKey(county);
      patch.countySelectionError = false;
      cachedDmas = {
        dma: county.dtvDMA,
        nielsenDma: county.nielsenDMA
      };
    }
    setState(patch);
  }

  function onStoreChange() {
    emitFee();
    const { zip } = store.getSnapshot();
    if (zip !== lastSyncedStoreZip) {
      lastSyncedStoreZip = zip;
      sync();
    } else {
      syncCommittedCounty();
    }
  }

  /* --- Success -------------------------------------------------------- */

  function showSuccess() {
    clearTimeout(successTimer);
    setState({ showSuccess: true });
    successTimer = setTimeout(() => {
      successTimer = undefined;
      setState({
        showSuccess: false,
        isExpanded: false
      });
    }, SUCCESS_MESSAGE_MS);
  }

  /* --- Public actions ------------------------------------------------- */

  function changeZip(raw: string) {
    const zip = sanitizeZipInput(raw);
    // The stale "no results" message goes as soon as the reader edits.
    setState({
      inputZip: zip,
      zipInputError: false,
      countyNoResults: false
    });
    if (isValidFullZip(zip)) {
      search(zip, "userinput");
    }
  }

  function selectCounty(key: string) {
    const county = state.countyOptions.find(item => countyKey(item) === key) ?? null;
    const patch: Partial<ZipSessionState> = {
      selectedCountyKey: county ? key : "",
      countySelectionError: false
    };
    // The reader trimmed the ZIP, then picked a county: restore the resolved ZIP.
    if (state.inputZip.length < 5 && cachedZipcode && cachedZipcode.includes(state.inputZip)) {
      patch.inputZip = cachedZipcode;
    }
    setState(patch);
    if (!county) {
      return;
    }
    if (cachedDmas?.dma !== county.dtvDMA || cachedDmas?.nielsenDma !== county.nielsenDMA) {
      cachedDmas = {
        dma: county.dtvDMA,
        nielsenDma: county.nielsenDMA
      };
      const zip = cachedZipcode;
      const key = channelsKey(zip, county.dtvDMA, county.nielsenDMA);
      const alreadyAsked = channelsInFlight.has(key) ||
        (cachedChannels !== null && channelsKey(cachedChannels.zip, cachedChannels.dma, cachedChannels.nielsenDma) === key);
      // The prefetch may already have asked for exactly this county: no second request.
      if (settings().fetchChannels && zip && !alreadyAsked) {
        channelsSeq += 1;
        const seq = channelsSeq;
        void loadChannels(zip, county.dtvDMA, county.nielsenDMA).then(data => {
          if (!started) {
            return;
          }
          // A submit waiting for exactly these delivers whatever came, failure included.
          if (isAwaited(zip, county.dtvDMA, county.nielsenDMA)) {
            deliverChannels(data, county.dtvDMA, county.nielsenDMA);
            channelsDeliveredSeq = Math.max(channelsDeliveredSeq, seq);
            awaitedChannels = null;
          }
          // A newer lookup started meanwhile owns the cache.
          if (seq === channelsSeq && data) {
            cachedChannels = {
              zip,
              dma: county.dtvDMA,
              nielsenDma: county.nielsenDMA,
              data
            };
          }
        });
      }
    }
  }

  /** Empties the field and the selection; the committed ZIP and the options stay. */
  function clear() {
    setState({
      inputZip: "",
      zipInputError: false,
      selectedCountyKey: ""
    });
  }

  function submit() {
    if (!isValidFullZip(state.inputZip)) {
      setState({ zipInputError: true });
      return;
    }
    /* Also with no counties at all. A selection left over from a ZIP whose
       lookup failed finds none either, because the options are emptied when a
       lookup starts — production submitted nothing here and said nothing. */
    const county = selectedCounty();
    if (!county) {
      setState({ countySelectionError: true });
      return;
    }

    const zip = cachedZipcode;
    const counties = state.countyOptions;
    const channels = cachedChannels &&
      cachedChannels.zip === zip && cachedChannels.dma === cachedDmas?.dma &&
      cachedChannels.nielsenDma === cachedDmas?.nielsenDma ? cachedChannels.data : null;

    setState({
      inputZip: zip,
      displayZip: zip,
      displayCounty: county
    });
    call(callbacks().onSearchButtonClick, zip, counties, feeFor(zip), channels, county);

    // Cookies before the store commit: the commit re-syncs, and sync reads them.
    if (cachedDmas && cachedDmas.dma !== "" && cachedDmas.nielsenDma !== "") {
      setDmaCookies(cachedDmas.dma, cachedDmas.nielsenDma);
    }
    store.commitZip(zip, counties, true, county);

    if (channels) {
      deliverChannels(channels, cachedDmas?.dma ?? "", cachedDmas?.nielsenDma ?? "");
      channelsDeliveredSeq = channelsSeq;
      awaitedChannels = null;
    } else if (settings().fetchChannels) {
      awaitedChannels = {
        zip,
        dma: county.dtvDMA,
        nielsenDma: county.nielsenDMA
      };
      // Nothing cached and nothing running for this key (a failed prefetch, say): start it.
      if (!channelsInFlight.has(channelsKey(zip, county.dtvDMA, county.nielsenDMA))) {
        void cacheLocale(zip, county, false);
      }
    }

    // Success depends on the ZIP and fee commit, not on channels arriving.
    showSuccess();
  }

  /* --- Lifecycle ------------------------------------------------------ */

  /** Starts syncing with the store and returns the stop function. Safe to call again after stop. */
  function start(config?: Parameters<ZipStore["init"]>[0]) {
    if (started) {
      return stop;
    }
    started = true;
    store.init(config);
    lastSyncedStoreZip = store.getSnapshot().zip;
    unsubscribe = store.subscribe(onStoreChange);
    emitFee();
    sync();
    return stop;
  }

  function stop() {
    if (!started) {
      return;
    }
    started = false;
    awaitedChannels = null;
    unsubscribe?.();
    unsubscribe = undefined;
    cancelLookup();
    clearTimeout(successTimer);
    successTimer = undefined;
    // A restart (Strict Mode) must not find a lookup that will never settle.
    setState({
      isCountyApiLoading: false,
      countyApiHanging: false,
      showSuccess: false
    });
  }

  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => state,
    getServerSnapshot: () => INITIAL_SESSION_STATE,
    /** Latest options win; callbacks are read at call time, so a new closure never restarts a timer. */
    setOptions(next: ZipSessionOptions) {
      options = next;
    },
    start,
    stop,
    setExpanded: (isExpanded: boolean) => setState({ isExpanded }),
    changeZip,
    selectCounty,
    clear,
    submit
  };
}

export type ZipSession = ReturnType<typeof createZipSession>;
